import type { SpreadsheetSheetProtectionOptions } from '../protocol/spreadsheetEngineProtocol'
import { formatCellAddress, normalizeSelectionRange, type SpreadsheetSelectionRange } from './addressing'
import type { ExcelValidationRuleDraft, ValidationRuleAuthoringApplyPlan } from './validationRuleAuthoring'

export type CellMetadataProjectionLike = {
  comment?: string | null
  hyperlink?: string | null
  protection?: Record<string, unknown> | null
}

export type CellMetadataScreenRect = {
  x: number
  y: number
  w: number
  h: number
}

export type CellMetadataViewport = {
  width: number
  height: number
}

export type CellMetadataVisibleBounds = {
  startCol: number
  endCol: number
  startRow: number
  endRow: number
}

export type HyperlinkPreviewPresentation = {
  label: string
  detail: string
  warning?: string
}

export type EffectiveCellProtectionStatus = {
  label: string
  detail: string
  explicitCellProtection: boolean
  locked: boolean
}

export type VisibleCommentEntry = {
  key: string
  address: string
  col: number
  row: number
  comment: string
  selected: boolean
}

export type CellMetadataActionId =
  | 'edit-comment'
  | 'clear-comment'
  | 'edit-hyperlink'
  | 'clear-hyperlink'
  | 'edit-protection'
  | 'clear-protection'

export type CellMetadataActionPresentation = {
  id: CellMetadataActionId
  label: string
  title: string
  enabled: boolean
}

export type CellMetadataDialogMode = 'comment' | 'hyperlink' | 'protection' | 'validation'

export type CellMetadataActionPlan =
  | { action: 'open-dialog'; mode: CellMetadataDialogMode }
  | { action: 'clear-comment' }
  | { action: 'clear-hyperlink' }
  | { action: 'clear-protection' }
  | { action: 'open-comments-pane' }
  | { action: 'open-hyperlink'; url?: string | null }

export type CellMetadataOpenHyperlinkActionPlan = Extract<CellMetadataActionPlan, { action: 'open-hyperlink' }>

export type CellMetadataDialogCopy = {
  title: string
  applyLabel: string
  clearLabel: string
  fieldLabel: string
}

export type CellMetadataDialogInitialState = {
  value: string
  locked: boolean
}

export type CellMetadataDialogApplyPlan =
  | { action: 'apply-comment'; comment: string | null }
  | { action: 'apply-hyperlink'; hyperlink: string | null }
  | { action: 'apply-protection'; protection: Record<string, unknown> | null }
  | { action: 'apply-validation-rule'; rule: ExcelValidationRuleDraft }
  | { action: 'blocked' }

export type CellMetadataDialogClearPlan =
  | { action: 'apply-comment'; comment: null }
  | { action: 'apply-hyperlink'; hyperlink: null }
  | { action: 'apply-protection'; protection: null }
  | { action: 'apply-validation'; validationId: null }

export type SelectedCellMetadataCardPresentation = {
  address: string
  title: string
  titleLines: string[]
  comment: string
  hyperlink: string
  hyperlinkPreview: HyperlinkPreviewPresentation | null
  protectionLabel: string
  protectionDetail: string
  hasExplicitProtection: boolean
  openableHyperlink: string | null
  actions: CellMetadataActionPresentation[]
  x: number
  y: number
}

export type CommentsPaneVisibleItemPresentation = {
  key: string
  address: string
  comment: string
  selected: boolean
  selectedLabel: string
  title: string
  col: number
  row: number
}

export type CommentsPanePresentation = {
  ariaLabel: string
  title: string
  reviewTitle: string
  visibleCountLabel: string
  closeAriaLabel: string
  closeTitle: string
  activeCellLabel: string
  activeAddress: string
  activeComment: string | null
  activeEmptyMessage: string
  editAction: CellMetadataActionPresentation
  clearAction: CellMetadataActionPresentation
  visibleListAriaLabel: string
  visibleEmptyMessage: string
  visibleItems: CommentsPaneVisibleItemPresentation[]
}

export const CELL_METADATA_DIALOG_COPY: Record<CellMetadataDialogMode, CellMetadataDialogCopy> = {
  comment: {
    title: 'Comment',
    applyLabel: 'Save Comment',
    clearLabel: 'Clear Comment',
    fieldLabel: 'Comment',
  },
  hyperlink: {
    title: 'Link',
    applyLabel: 'Apply Link',
    clearLabel: 'Clear Link',
    fieldLabel: 'Address',
  },
  protection: {
    title: 'Protect Cells',
    applyLabel: 'Apply',
    clearLabel: 'Use Default',
    fieldLabel: 'Locked',
  },
  validation: {
    title: 'Data Validation',
    applyLabel: 'Apply Rule',
    clearLabel: 'Clear Rule',
    fieldLabel: 'Rule',
  },
}

export type CellMetadataCommandId = 'hyperlink' | 'comments' | 'protect-cells' | 'protect-sheet'

export type CellMetadataCommandPresentation = Record<CellMetadataCommandId, {
  enabled: boolean
  disabledReason: string
  active?: boolean
}>

export type CellMetadataContextMenuCommandId =
  | 'open-hyperlink'
  | 'hyperlink'
  | 'remove-hyperlink'
  | 'comments'
  | 'clear-comment'
  | 'protect-cells'
  | 'clear-cell-protection'

export type CellMetadataContextMenuEntry = {
  commandId: CellMetadataContextMenuCommandId
  action: CellMetadataContextMenuCommandId
  label?: string
  title?: string
}

export type CellMetadataRangeContextMenuEntry = {
  commandId: 'protect-cells'
  action: 'protect-cells'
}

const OPEN_HYPERLINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:'])

function defaultHyperlinkBaseHref(): string {
  return typeof window !== 'undefined' ? window.location.href : 'https://fusegrid.local/'
}

export function toOpenableHyperlinkUrl(
  hyperlink?: string | null,
  baseHref = defaultHyperlinkBaseHref(),
): string | null {
  const trimmed = String(hyperlink ?? '').trim()
  if (!trimmed) return null
  const hasExplicitScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed)
  const candidate = hasExplicitScheme ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(candidate, baseHref)
    if (!OPEN_HYPERLINK_PROTOCOLS.has(url.protocol)) return null
    return url.href
  } catch {
    return null
  }
}

export function describeHyperlinkPreview(
  hyperlink?: string | null,
  baseHref = defaultHyperlinkBaseHref(),
): HyperlinkPreviewPresentation | null {
  const raw = String(hyperlink ?? '').trim()
  if (!raw) return null
  const openable = toOpenableHyperlinkUrl(raw, baseHref)
  if (!openable) {
    return {
      label: raw,
      detail: 'This link is saved as cell metadata but cannot be opened until it uses http, https, mailto, or tel.',
      warning: 'Unsupported link target',
    }
  }
  try {
    const url = new URL(openable)
    if (url.protocol === 'mailto:') {
      return { label: url.pathname, detail: `Email link: ${openable}` }
    }
    if (url.protocol === 'tel:') {
      return { label: url.pathname, detail: `Phone link: ${openable}` }
    }
    return {
      label: url.hostname,
      detail: `${url.protocol.replace(':', '')}://${url.host}${url.pathname}${url.search}${url.hash}`,
    }
  } catch {
    return { label: raw, detail: openable }
  }
}

export function describeCellProtection(protection?: Record<string, unknown> | null): string | null {
  if (!protection) return null
  if (Object.prototype.hasOwnProperty.call(protection, 'locked')) {
    return protection.locked === false ? 'Unlocked cell' : 'Locked cell'
  }
  return 'Protected cell'
}

export function cellProtectionIsLocked(protection?: Record<string, unknown> | null): boolean {
  if (!protection) return false
  if (Object.prototype.hasOwnProperty.call(protection, 'locked')) {
    return protection.locked !== false
  }
  return true
}

export function buildCellMetadataDialogInitialState(args: {
  mode: CellMetadataDialogMode
  currentComment?: string | null
  currentHyperlink?: string | null
  currentProtection?: Record<string, unknown> | null
}): CellMetadataDialogInitialState {
  if (args.mode === 'comment') {
    return { value: args.currentComment ?? '', locked: true }
  }
  if (args.mode === 'hyperlink') {
    return { value: args.currentHyperlink ?? '', locked: true }
  }
  if (args.mode === 'protection') {
    return { value: '', locked: cellProtectionIsLocked(args.currentProtection) }
  }
  return { value: '', locked: true }
}

export function buildCellMetadataDialogApplyPlan(args: {
  mode: CellMetadataDialogMode
  value: string
  locked: boolean
  validationApplyPlan?: ValidationRuleAuthoringApplyPlan | null
}): CellMetadataDialogApplyPlan {
  if (args.mode === 'comment') {
    return { action: 'apply-comment', comment: args.value.trim() || null }
  }
  if (args.mode === 'hyperlink') {
    return { action: 'apply-hyperlink', hyperlink: args.value.trim() || null }
  }
  if (args.mode === 'validation') {
    const validationPlan = args.validationApplyPlan
    return validationPlan?.kind === 'applyRule'
      ? { action: 'apply-validation-rule', rule: validationPlan.rule }
      : { action: 'blocked' }
  }
  return {
    action: 'apply-protection',
    protection: args.locked ? { locked: true } : { locked: false },
  }
}

export function buildCellMetadataDialogClearPlan(
  mode: CellMetadataDialogMode,
): CellMetadataDialogClearPlan {
  if (mode === 'comment') return { action: 'apply-comment', comment: null }
  if (mode === 'hyperlink') return { action: 'apply-hyperlink', hyperlink: null }
  if (mode === 'validation') return { action: 'apply-validation', validationId: null }
  return { action: 'apply-protection', protection: null }
}

export function sheetProtectionIsEnabled(protection?: Record<string, unknown> | null): boolean {
  if (!protection) return false
  if (Object.prototype.hasOwnProperty.call(protection, 'enabled')) {
    return protection.enabled !== false
  }
  return true
}

export function describeEffectiveCellProtection(
  cellProtection: Record<string, unknown> | null | undefined,
  sheetProtection: SpreadsheetSheetProtectionOptions | null | undefined,
  sheetName: string,
): EffectiveCellProtectionStatus | null {
  const explicitLabel = describeCellProtection(cellProtection)
  const sheetProtected = sheetProtectionIsEnabled(sheetProtection)
  if (explicitLabel) {
    const unlocked = cellProtection?.locked === false
    if (unlocked) {
      return {
        label: 'Unlocked cell',
        detail: sheetProtected
          ? `Cell is explicitly unlocked while sheet "${sheetName}" is protected.`
          : 'Cell has an explicit unlocked-cell protection override.',
        explicitCellProtection: true,
        locked: false,
      }
    }
    return {
      label: explicitLabel,
      detail: 'Cell has an explicit locked-cell protection rule.',
      explicitCellProtection: true,
      locked: true,
    }
  }
  if (sheetProtected) {
    return {
      label: 'Locked by sheet',
      detail: `Sheet "${sheetName}" is protected; cells are locked by default unless explicitly unlocked.`,
      explicitCellProtection: false,
      locked: true,
    }
  }
  return null
}

export function buildCellMetadataCommandPresentation(args: {
  hasSelection?: boolean | null
  hasStreamer?: boolean | null
  activeSheetProtected?: boolean | null
}): CellMetadataCommandPresentation {
  const hasSelection = Boolean(args.hasSelection)
  const hasStreamer = Boolean(args.hasStreamer)
  return {
    hyperlink: {
      enabled: hasSelection && hasStreamer,
      disabledReason: 'Select a cell after the spreadsheet engine is ready',
    },
    comments: {
      enabled: hasSelection && hasStreamer,
      disabledReason: 'Select a cell after the spreadsheet engine is ready',
    },
    'protect-cells': {
      enabled: hasSelection && hasStreamer,
      disabledReason: 'Select cells after the spreadsheet engine is ready',
    },
    'protect-sheet': {
      enabled: hasStreamer,
      active: Boolean(args.activeSheetProtected),
      disabledReason: 'Spreadsheet engine is not ready',
    },
  }
}

export function buildCellMetadataContextMenuEntries(args: {
  metadata?: CellMetadataProjectionLike | null
  hyperlinkBaseHref?: string
}): CellMetadataContextMenuEntry[] {
  const hyperlink = String(args.metadata?.hyperlink ?? '').trim()
  const comment = String(args.metadata?.comment ?? '').trim()
  const hasProtection = args.metadata?.protection != null
  const openableHyperlink = toOpenableHyperlinkUrl(hyperlink, args.hyperlinkBaseHref)
  return [
    ...(openableHyperlink
      ? [{
        commandId: 'open-hyperlink' as const,
        action: 'open-hyperlink' as const,
        title: `Open ${hyperlink || 'link'}`,
      }]
      : []),
    {
      commandId: 'hyperlink',
      action: 'hyperlink',
    },
    ...(hyperlink
      ? [{
        commandId: 'remove-hyperlink' as const,
        action: 'remove-hyperlink' as const,
      }]
      : []),
    {
      commandId: 'comments',
      action: 'comments',
      label: 'Comment',
    },
    ...(comment
      ? [{
        commandId: 'clear-comment' as const,
        action: 'clear-comment' as const,
      }]
      : []),
    {
      commandId: 'protect-cells',
      action: 'protect-cells',
    },
    ...(hasProtection
      ? [{
        commandId: 'clear-cell-protection' as const,
        action: 'clear-cell-protection' as const,
      }]
      : []),
  ]
}

export function buildCellMetadataRangeContextMenuEntries(): CellMetadataRangeContextMenuEntry[] {
  return [{
    commandId: 'protect-cells',
    action: 'protect-cells',
  }]
}

export function buildCellMetadataActionPlan(
  actionId: CellMetadataActionId | CellMetadataContextMenuCommandId | string,
): CellMetadataActionPlan | null {
  switch (actionId) {
    case 'edit-comment':
      return { action: 'open-dialog', mode: 'comment' }
    case 'clear-comment':
      return { action: 'clear-comment' }
    case 'edit-hyperlink':
    case 'hyperlink':
      return { action: 'open-dialog', mode: 'hyperlink' }
    case 'clear-hyperlink':
    case 'remove-hyperlink':
      return { action: 'clear-hyperlink' }
    case 'edit-protection':
    case 'protect-cells':
      return { action: 'open-dialog', mode: 'protection' }
    case 'clear-protection':
    case 'clear-cell-protection':
      return { action: 'clear-protection' }
    case 'comments':
      return { action: 'open-comments-pane' }
    case 'open-hyperlink':
      return { action: 'open-hyperlink' }
    default:
      return null
  }
}

export function buildCellMetadataOpenHyperlinkPlan(
  hyperlink?: string | null,
  baseHref?: string,
): CellMetadataOpenHyperlinkActionPlan | null {
  const url = toOpenableHyperlinkUrl(hyperlink, baseHref)
  return url ? { action: 'open-hyperlink', url } : null
}

export function buildSelectedMetadataCardOpenHyperlinkPlan(
  card?: Pick<SelectedCellMetadataCardPresentation, 'openableHyperlink'> | null,
): CellMetadataOpenHyperlinkActionPlan | null {
  return buildCellMetadataOpenHyperlinkPlan(card?.openableHyperlink)
}

function commentFromMetadata(metadata?: CellMetadataProjectionLike | null): string {
  return String(metadata?.comment ?? '').trim()
}

export function buildSelectedCommentEntry(args: {
  selection?: SpreadsheetSelectionRange | null
  metadata?: CellMetadataProjectionLike | null
}): VisibleCommentEntry | null {
  const comment = commentFromMetadata(args.metadata)
  if (!comment || !args.selection) return null
  const selection = normalizeSelectionRange(args.selection)
  return {
    key: `${selection.col}:${selection.row}`,
    address: formatCellAddress(selection.col, selection.row),
    col: selection.col,
    row: selection.row,
    comment,
    selected: true,
  }
}

export function buildVisibleCommentEntries(args: {
  disabled?: boolean
  visibleBounds: CellMetadataVisibleBounds
  selection?: SpreadsheetSelectionRange | null
  getCellMetadata: (col: number, row: number) => CellMetadataProjectionLike | undefined
}): VisibleCommentEntry[] {
  if (args.disabled) return []
  const entries: VisibleCommentEntry[] = []
  const seen = new Set<string>()
  const normalizedSelection = args.selection ? normalizeSelectionRange(args.selection) : null
  const addEntry = (col: number, row: number, selected: boolean) => {
    const key = `${col}:${row}`
    const comment = commentFromMetadata(args.getCellMetadata(col, row))
    if (!comment || seen.has(key)) return
    seen.add(key)
    entries.push({
      key,
      address: formatCellAddress(col, row),
      col,
      row,
      comment,
      selected,
    })
  }

  if (normalizedSelection) {
    addEntry(normalizedSelection.col, normalizedSelection.row, true)
  }

  for (let row = args.visibleBounds.startRow; row <= args.visibleBounds.endRow; row += 1) {
    for (let col = args.visibleBounds.startCol; col <= args.visibleBounds.endCol; col += 1) {
      const selected = Boolean(normalizedSelection && normalizedSelection.col === col && normalizedSelection.row === row)
      addEntry(col, row, selected)
    }
  }

  return entries.sort((left, right) => {
    if (left.selected !== right.selected) return left.selected ? -1 : 1
    if (left.row !== right.row) return left.row - right.row
    return left.col - right.col
  })
}

function buildSelectedMetadataCardActions(args: {
  hasComment: boolean
  hasHyperlink: boolean
  hasProtection: boolean
  hasExplicitProtection: boolean
}): CellMetadataActionPresentation[] {
  const actions: CellMetadataActionPresentation[] = []
  if (args.hasComment) {
    actions.push({
      id: 'edit-comment',
      label: 'Edit Comment',
      title: 'Edit comment',
      enabled: true,
    }, {
      id: 'clear-comment',
      label: 'Clear Comment',
      title: 'Clear comment',
      enabled: true,
    })
  }
  if (args.hasHyperlink) {
    actions.push({
      id: 'edit-hyperlink',
      label: 'Edit Link',
      title: 'Edit link',
      enabled: true,
    }, {
      id: 'clear-hyperlink',
      label: 'Remove Link',
      title: 'Remove link',
      enabled: true,
    })
  }
  if (args.hasProtection) {
    actions.push({
      id: 'edit-protection',
      label: 'Edit Protection',
      title: 'Edit protection',
      enabled: true,
    })
  }
  if (args.hasExplicitProtection) {
    actions.push({
      id: 'clear-protection',
      label: 'Use Default',
      title: 'Use default protection',
      enabled: true,
    })
  }
  return actions
}

export function buildSelectedCellMetadataCardPresentation(args: {
  disabled?: boolean
  selection?: SpreadsheetSelectionRange | null
  metadata?: CellMetadataProjectionLike | null
  protectionStatus?: EffectiveCellProtectionStatus | null
  cellRect?: CellMetadataScreenRect | null
  viewport?: CellMetadataViewport | null
  hyperlinkBaseHref?: string
}): SelectedCellMetadataCardPresentation | null {
  if (args.disabled || !args.selection) return null
  const selection = normalizeSelectionRange(args.selection)
  const comment = String(args.metadata?.comment ?? '').trim()
  const hyperlink = String(args.metadata?.hyperlink ?? '').trim()
  const protectionStatus = args.protectionStatus && (
    args.protectionStatus.explicitCellProtection ||
    Boolean(comment) ||
    Boolean(hyperlink)
  )
    ? args.protectionStatus
    : null
  if (!comment && !hyperlink && !protectionStatus) return null
  if (!args.cellRect || !args.viewport) return null
  const maxX = Math.max(8, args.viewport.width - 300)
  const maxY = Math.max(8, args.viewport.height - 150)
  const protectionLabel = protectionStatus?.label ?? ''
  const hasExplicitProtection = protectionStatus?.explicitCellProtection === true
  return {
    address: formatCellAddress(selection.col, selection.row),
    title: comment ? 'Comment' : hyperlink ? 'Link' : 'Protection',
    titleLines: [
      comment ? `Comment: ${comment}` : '',
      hyperlink ? `Link: ${hyperlink}` : '',
      protectionLabel ? `Protection: ${protectionLabel}` : '',
    ].filter(Boolean),
    comment,
    hyperlink,
    hyperlinkPreview: describeHyperlinkPreview(hyperlink, args.hyperlinkBaseHref),
    protectionLabel,
    protectionDetail: protectionStatus?.detail ?? '',
    hasExplicitProtection,
    openableHyperlink: toOpenableHyperlinkUrl(hyperlink, args.hyperlinkBaseHref),
    actions: buildSelectedMetadataCardActions({
      hasComment: Boolean(comment),
      hasHyperlink: Boolean(hyperlink),
      hasProtection: Boolean(protectionLabel),
      hasExplicitProtection,
    }),
    x: Math.max(8, Math.min(maxX, args.cellRect.x + args.cellRect.w + 8)),
    y: Math.max(8, Math.min(maxY, args.cellRect.y)),
  }
}

export function buildCommentsPanePresentation(args: {
  selectedRangeLabel: string
  selectedCommentEntry?: VisibleCommentEntry | null
  visibleCommentEntries: VisibleCommentEntry[]
}): CommentsPanePresentation {
  const visibleItems = args.visibleCommentEntries.map((entry) => ({
    key: entry.key,
    address: entry.address,
    comment: entry.comment,
    selected: entry.selected,
    selectedLabel: entry.selected ? 'Active' : '',
    title: `Select ${entry.address}: ${entry.comment}`,
    col: entry.col,
    row: entry.row,
  }))
  const count = visibleItems.length
  const hasSelectedComment = Boolean(args.selectedCommentEntry)
  return {
    ariaLabel: 'Comments',
    title: 'Comments',
    reviewTitle: 'Review visible engine-backed cell comments',
    visibleCountLabel: `${count} visible comment${count === 1 ? '' : 's'}`,
    closeAriaLabel: 'Close comments pane',
    closeTitle: 'Close comments pane',
    activeCellLabel: 'Active cell',
    activeAddress: args.selectedRangeLabel || 'A1',
    activeComment: args.selectedCommentEntry?.comment ?? null,
    activeEmptyMessage: 'No comment is stored on the active cell.',
    editAction: {
      id: 'edit-comment',
      label: hasSelectedComment ? 'Edit Comment' : 'Add Comment',
      title: hasSelectedComment ? 'Edit comment on the active cell' : 'Add a comment to the active cell',
      enabled: true,
    },
    clearAction: {
      id: 'clear-comment',
      label: 'Clear',
      title: hasSelectedComment ? 'Clear comment from the active cell' : 'No active-cell comment to clear',
      enabled: hasSelectedComment,
    },
    visibleListAriaLabel: 'Visible comments',
    visibleEmptyMessage: 'No comments are visible in the current viewport.',
    visibleItems,
  }
}
